import React, { useState, useEffect } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Button, Box, Slide, CircularProgress } from "@mui/material";
import { createWhatsAppMessage } from "../../utils/urlShortener";
import { useSelector } from "react-redux";

const SuccessDialog = ({ isOpen, onClose, pdfUrl, headerText = "Renewal Success", messageText = "Policy renewal completed. Share with customer via WhatsApp.", agentId, policyId, premiumAmount, balanceAmount }) => {
  const { user } = useSelector((state) => state.auth);
  const [isSharing, setIsSharing] = useState(false);
  const [agentName, setAgentName] = useState("Your Agent");

  // Get agent's name from Redux store
  useEffect(() => {
    console.log('Full user object from Redux:', user);
    console.log('user.displayName:', user?.displayName);
    console.log('user.name:', user?.name);

    if (user?.displayName && user.displayName !== '') {
      console.log('Using agent name from Redux store (displayName):', user.displayName);
      setAgentName(user.displayName);
    } else if (user?.name && user.name !== '') {
      console.log('Using agent name from Redux store (name field):', user.name);
      setAgentName(user.name);
    } else {
      console.log('No agent name found in Redux store, fetching from Firestore...');
      // Fallback to Firestore if Redux doesn't have the name
      fetchAgentNameFromFirestore();
    }
  }, [user]);

  const fetchAgentNameFromFirestore = async () => {
    if (!user?.uid) return;

    try {
      console.log('Fetching agent name from Firestore for UID:', user.uid);

      // Try to get from agent-details collection using UID
      const agentDoc = await getDoc(doc(db, 'agent-details', user.uid));

      if (agentDoc.exists()) {
        const agentData = agentDoc.data();
        console.log('Agent data from Firestore:', agentData);

        if (agentData.name) {
          console.log('Using agent name from Firestore:', agentData.name);
          setAgentName(agentData.name);
          return;
        } else if (agentData.displayName) {
          console.log('Using agent displayName from Firestore:', agentData.displayName);
          setAgentName(agentData.displayName);
          return;
        }
      } else {
        console.log('No agent document found in Firestore for UID:', user.uid);
      }

      console.log('Using default agent name');
      setAgentName('Your Agent');
    } catch (error) {
      console.error('Error fetching agent name from Firestore:', error);
      setAgentName('Your Agent');
    }
  };

  // Handle share: resolve URL on click, generate message, then open WhatsApp
  const handleShare = async () => {
    if (!pdfUrl || isSharing) return;

    setIsSharing(true);

    try {
      let shareUrl = pdfUrl;

      // If the PDF is on Google Drive, create a temporary shareable link
      // This matches the behavior in PolicyDetail.handleSharePdf
      if (pdfUrl.includes('drive.google.com')) {
        try {
          const driveMatch = pdfUrl.match(/\/d\/([a-zA-Z0-9_-]+)/);
          if (driveMatch && driveMatch[1]) {
            const driveFileId = driveMatch[1];
            console.log("📁 Drive file detected, creating share link for:", driveFileId);
            const { createDriveShareLink } = await import("../../utils/driveApi");
            const shareLinks = await createDriveShareLink(driveFileId);
            shareUrl = shareLinks.shareableLink;
            console.log("✅ Drive share link created:", shareUrl);
          }
        } catch (driveError) {
          console.error("❌ Failed to create Drive share link:", driveError);
          // Fall back to original pdfUrl
          shareUrl = pdfUrl;
        }
      }
      // For Firebase Storage URLs, use pdfUrl directly (already publicly accessible)

      // Generate WhatsApp message with short URL
      let shareText;
      try {
        shareText = await createWhatsAppMessage(shareUrl, agentName, policyId, false, premiumAmount, balanceAmount);
      } catch (msgError) {
        console.error("Error generating WhatsApp message:", msgError);
        // Fallback message
        shareText = agentName && agentName !== "Your Agent"
          ? `Your policy has been renewed successfully by ${agentName} ✅\nPremium - ₹${premiumAmount || 0}, Balance - ₹${balanceAmount || 0}\nTap below to download your renewed policy 📄:\n${shareUrl}`
          : `Your policy has been renewed successfully ✅\nPremium - ₹${premiumAmount || 0}, Balance - ₹${balanceAmount || 0}\nTap below to download your renewed policy 📄:\n${shareUrl}`;
      }

      // Open WhatsApp
      const whatsappAppUrl = `whatsapp://send?text=${encodeURIComponent(shareText)}`;
      const webWhatsappUrl = `https://web.whatsapp.com/send?text=${encodeURIComponent(shareText)}`;

      // Try app first using a hidden iframe to avoid console errors
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = whatsappAppUrl;
      document.body.appendChild(iframe);

      // Remove iframe after a short delay
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 100);

      // Listen for page blur (indicates app opened)
      let appOpened = false;
      const handleBlur = () => {
        appOpened = true;
        window.removeEventListener('blur', handleBlur);
      };
      window.addEventListener('blur', handleBlur);

      // Fallback to web after 1.5 seconds ONLY if app didn't open
      setTimeout(() => {
        window.removeEventListener('blur', handleBlur);
        if (!appOpened) {
          window.open(webWhatsappUrl, "_blank", "noopener,noreferrer");
        }
      }, 1500);
    } catch (error) {
      console.error("Error sharing PDF:", error);
    } finally {
      setIsSharing(false);
    }
  };

  const Transition = React.useMemo(() =>
    React.forwardRef(function Transition(props, ref) {
      return <Slide direction="up" ref={ref} {...props} />;
    }), []);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Transition}
      TransitionProps={{ timeout: 300 }}
      PaperProps={{
        sx: {
          p: 4,
          boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.12)",
          borderRadius: 2,
          minHeight: 300,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        },
      }}
    >
      <DialogTitle
        className="success-dialog-header"
        sx={{
          fontFamily: "Roboto Slab !important",
          fontWeight: "500 !important",
          fontStyle: "Medium",
          fontSize: "32px !important",
          lineHeight: "56px !important",
          letterSpacing: "2% !important",
          color: "#000000 !important",
          mt: 0.5,
          mb: 2,
          textAlign: "left !important",
        }}
      >
        {headerText}
      </DialogTitle>
      <DialogContent sx={{ flexGrow: 1, pb: 3 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexWrap="wrap"
          textAlign="center"
          sx={{
            fontSize: 18,
            lineHeight: "26px",
            fontWeight: 400,
            fontFamily: "Roboto",
            color: "#000000",
            mb: 2,
          }}
        >
          {messageText}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "flex-end",
          gap: 2,
          pt: 2,
          pr: 3,
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            width: 160,
            height: 56,
            borderColor: "#0063F7",
            color: "#0063F7",
            fontSize: 18,
            fontFamily: "Roboto",
            fontWeight: 600,
            textTransform: "none",
            "&:hover": {
              borderColor: "#0063F7",
              backgroundColor: "rgba(0, 99, 247, 0.08)",
            },
          }}
        >
          Close
        </Button>

        <Button
          variant="contained"
          onClick={handleShare}
          disabled={!pdfUrl || isSharing}
          sx={{
            width: 250, // Fixed width
            height: 56,
            backgroundColor: "#0063F7 !important",
            color: "#fff",
            fontSize: 18,
            fontFamily: "Roboto",
            fontWeight: 600,
            textTransform: "none",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            whiteSpace: "nowrap",
            px: 2,
            "&:hover": {
              backgroundColor: "#0051d4 !important",
            },
            "&:disabled": {
              backgroundColor: "#cccccc !important",
              color: "#666666 !important",
            },
          }}
        >
          <FaWhatsapp size={25} />
          <Box sx={{ width: 170, display: "flex", justifyContent: "center", ml: 1.5 }}>
            {isSharing ? (
              <CircularProgress
                size={24}
                thickness={5}
                sx={{ color: "#fff" }}
              />
            ) : (
              "Share Via Whatsapp"
            )}
          </Box>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SuccessDialog;